import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SEOHead, getOrganizationSchema, getWebPageSchema, getBreadcrumbListSchema, getFAQPageSchema } from '@/components/base/SEOHead';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const hpField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (hpField && hpField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d95n0rt7gtqne2as9l10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = { code: 'Unknown', meta: { message: responseText } };
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('success');
          form.reset();
          setTimeout(() => setFormStatus('idle'), 5000);
          return;
        }
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const faqs = [
    {
      question: '五一黑料网的内容来源是什么？',
      answer: '五一黑料网的内容来源于用户投稿、编辑团队采编以及公开网络信息整合。我们拥有专业的编辑团队，对每一条信息进行严格审核和交叉验证，确保内容的真实性和时效性。',
    },
    {
      question: '如何向五一黑料网投稿或爆料？',
      answer: '你可以通过本页面的商务合作表单提交爆料信息，或者直接发送邮件到我们的官方邮箱。我们承诺对所有爆料人信息严格保密，同时会对线索进行专业核实后再发布。',
    },
    {
      question: '五一黑料网的内容更新频率如何？',
      answer: '五一黑料网实行7×24小时内容更新机制，编辑团队全天候追踪最新娱乐动态。平均每天发布超过50条独家内容和热点追踪，确保用户第一时间获取最新吃瓜资讯。',
    },
    {
      question: '发现有误或侵权内容怎么办？',
      answer: '如果你发现平台上的内容存在事实错误或侵犯了你的合法权益，请立即通过商务合作表单或官方邮箱联系我们。我们会在24小时内核实并处理，确需更正的会及时发布更正声明。',
    },
    {
      question: '五一黑料网与其他吃瓜平台有什么区别？',
      answer: '五一黑料网坚持"真实、深度、独家"的内容理念。我们不仅报道热点事件，更注重深度调查和独家挖掘。同时，我们建立了严格的内容审核机制，在追求速度的同时绝不牺牲准确性。',
    },
  ];

  const pageTitle = '关于我们 - 五一黑料网';
  const pageDesc = '了解五一黑料网品牌故事、平台数据、商务合作及联系方式。五一黑料网 - 汇聚全网最热吃瓜资源，权威发布独家爆料内容。';
  const pageUrl = 'https://www.51heiliao.com/about';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      getOrganizationSchema(),
      getWebPageSchema(pageTitle, pageDesc, pageUrl),
      getBreadcrumbListSchema([
        { name: '首页', url: 'https://www.51heiliao.com/' },
        { name: '关于我们', url: pageUrl },
      ]),
      getFAQPageSchema(faqs),
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords="五一黑料网,关于我们,品牌介绍,商务合作,联系我们,爆料平台"
        canonicalUrl={pageUrl}
        schema={pageSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="px-4 md:px-6 py-3">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Brand Intro Header */}
          <section className="px-4 md:px-6 py-8 md:py-12" aria-label="品牌介绍">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
                关于 <span className="text-primary-500">五一黑料网</span>
              </h1>
              <p className="text-lg md:text-xl text-foreground-500 leading-relaxed max-w-2xl mx-auto">
                国内领先的娱乐资讯聚合平台，专注网红动态、娱乐八卦与深度爆料内容
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="px-4 md:px-6 pb-8 md:pb-10" aria-label="品牌故事">
            <div className="max-w-3xl mx-auto">
              <div className="bg-background-100 rounded-lg border border-background-300/30 p-6 md:p-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                  <p>
                    五一黑料网成立于2024年5月1日，由一群热爱娱乐资讯的资深媒体人联合创办。我们致力于打造一个真实、及时、全面的娱乐内容平台，为亿万网友提供第一手吃瓜资讯。
                  </p>
                  <p>
                    在发展过程中，五一黑料网始终坚持"内容为王"的核心理念。我们的编辑团队拥有超过10年的娱乐媒体从业经验，与各大平台、MCN机构和网红圈层保持着紧密的信息网络。无论是网红翻车现场、娱乐圈黑幕曝光，还是每日热点赛事追踪，我们都能第一时间为你带来深度报道。
                  </p>
                  <p>
                    从创立至今，五一黑料网已累计发布超过5万条独家内容，月活跃用户突破1200万，稳居国内娱乐资讯平台第一梯队。我们不仅是一个资讯平台，更是万千网友的"吃瓜大本营"。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Platform Stats */}
          <section className="px-4 md:px-6 pb-8 md:pb-10" aria-label="平台数据">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 text-center">
                平台数据
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: '50,000+', label: '独家内容', icon: 'ri-article-line' },
                  { value: '1200万+', label: '月活用户', icon: 'ri-user-line' },
                  { value: '100+', label: '合作媒体', icon: 'ri-building-line' },
                  { value: '99.7%', label: '内容准确率', icon: 'ri-shield-check-line' },
                ].map((stat, i) => (
                  <div key={i} className="bg-background-100 rounded-lg border border-background-300/30 p-5 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="px-4 md:px-6 pb-8 md:pb-10" aria-label="发展历程">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 text-center">
                发展历程
              </h2>
              <div className="space-y-0">
                {[
                  {
                    year: '2024年5月',
                    title: '平台正式上线',
                    desc: '五一黑料网正式成立，首月即获得50万用户关注',
                  },
                  {
                    year: '2024年9月',
                    title: '月活突破500万',
                    desc: '凭借独家深度报道，平台影响力快速扩大，月活跃用户突破500万',
                  },
                  {
                    year: '2025年1月',
                    title: '完成品牌升级',
                    desc: '全面升级内容体系和用户体验，引入智能推荐算法',
                  },
                  {
                    year: '2025年6月',
                    title: '月活突破1000万',
                    desc: '稳居国内娱乐资讯平台前列，单日最高PV突破3000万',
                  },
                  {
                    year: '2026年1月',
                    title: '生态全面升级',
                    desc: '拓展多元内容生态，覆盖短视频、直播、图文等多种内容形态',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-accent-500 mt-1.5 flex-shrink-0"></div>
                      {i < 4 && <div className="w-px flex-1 bg-background-300/60 my-1"></div>}
                    </div>
                    <div className="pb-6">
                      <span className="text-xs font-medium text-accent-500 whitespace-nowrap">{item.year}</span>
                      <h3 className="text-base font-semibold text-foreground-950 mt-0.5">{item.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Business Cooperation */}
          <section className="px-4 md:px-6 pb-8 md:pb-10" aria-label="商务合作">
            <div className="max-w-4xl mx-auto">
              <div className="bg-background-100 rounded-lg border border-background-300/30 p-6 md:p-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
                  商务合作 &amp; 联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Contact Info */}
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                      合作方式
                    </h3>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-500 flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-950">商务合作邮箱</p>
                          <p className="text-sm text-foreground-500">bd@51heiliao.com</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-500 flex-shrink-0">
                          <i className="ri-phone-line"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-950">合作热线</p>
                          <p className="text-sm text-foreground-500">400-888-5151</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-500 flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-950">公司地址</p>
                          <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 p-4 rounded-lg bg-background-50 border border-background-300/30">
                      <h4 className="text-sm font-semibold text-foreground-950 mb-2">商务合作范围</h4>
                      <ul className="space-y-1.5 text-sm text-foreground-500">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                          内容合作：独家爆料、联合报道
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                          广告合作：品牌推广、软文植入
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                          MCN合作：网红孵化、流量扶持
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                          媒体合作：内容互换、联合活动
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Contact Form */}
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                      在线留言
                    </h3>
                    <form
                      data-readdy-form
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      {/* Honeypot */}
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="contact-form-hp"
                      />

                      <div>
                        <label htmlFor="cooperation-type" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          合作类型
                        </label>
                        <select
                          id="cooperation-type"
                          name="cooperation_type"
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20"
                        >
                          <option value="">请选择合作类型</option>
                          <option value="content">内容合作</option>
                          <option value="ad">广告合作</option>
                          <option value="mcn">MCN合作</option>
                          <option value="media">媒体合作</option>
                          <option value="other">其他</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          联系人姓名
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20"
                          placeholder="请输入姓名"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          联系邮箱
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20"
                          placeholder="请输入邮箱"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          联系电话
                        </label>
                        <input
                          type="tel"
                          id="contact-phone"
                          name="phone"
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20"
                          placeholder="请输入电话"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 resize-none"
                          placeholder="请描述你的合作需求（限500字）"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        提交留言
                      </button>

                      {formStatus === 'success' && (
                        <p className="text-sm text-accent-500 text-center">
                          提交成功！我们会尽快与你联系。
                        </p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-primary-500 text-center">
                          {formError}
                        </p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="px-4 md:px-6 pb-10" aria-label="常见问题">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-lg border border-background-300/30 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                      <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                        {faq.question}
                      </span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}